import { useEffect, useRef, useState, useCallback } from "react";
import type { DocKeystrokeState } from "../state/types";

/**
 * Keystroke capture hook for the verification station.
 *
 * Tracks countable keypresses (typed chars + navigational keys) per document.
 * Two threshold constants tune realism:
 *
 *  - CLAMP_MS (30): pairs of keydowns closer than this are dropped as
 *    autocomplete / stuck-key bursts. Prevents inflated counts.
 *  - IDLE_THRESHOLD_MS (3_000): gaps longer than this between keydowns are
 *    considered idle, not active keying. Backs the "active keying time"
 *    metric that analytics exposes.
 *
 * Resets state when documentId (or step) changes. Calls onFlush with the
 * current state on `beforeunload` so a hard tab close still emits a snapshot.
 */

const CLAMP_MS = 30;
const IDLE_THRESHOLD_MS = 3000;

const NAV_KEYS = new Set([
  "Backspace",
  "Delete",
  "Enter",
  "Tab",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
]);

export function isCountableKey(e: KeyboardEvent): boolean {
  if (e.key.length === 1) return true;
  return NAV_KEYS.has(e.key);
}

interface Args {
  documentId: string;
  step: string;
  active: boolean;
  onFlush?: (state: DocKeystrokeState) => void;
}

function newState(documentId: string, step: string): DocKeystrokeState {
  return {
    documentId,
    firstKeystrokeAt: null,
    lastKeystrokeAt: null,
    keystrokeCount: 0,
    activeKeyingMs: 0,
    idleMs: 0,
    fieldTimings: new Map(),
    currentStep: step,
  };
}

export function useVerificationKeystrokes(args: Args): DocKeystrokeState {
  const [state, setState] = useState<DocKeystrokeState>(() =>
    newState(args.documentId, args.step),
  );

  // Reset when documentId or step changes.
  useEffect(() => {
    setState(newState(args.documentId, args.step));
  }, [args.documentId, args.step]);

  // Track whether the tab is hidden so the next-keystroke gap is charged to
  // idle rather than active keying. We don't freeze the whole hook because
  // that loses counts for inputs delivered while hidden (unlikely but cheap
  // insurance).
  const hiddenRef = useRef(false);
  useEffect(() => {
    const onVisibility = () => {
      hiddenRef.current = document.visibilityState === "hidden";
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const onKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (!args.active) return;
      if (!isCountableKey(e)) return;
      const now = Date.now();
      setState((prev) => {
        if (prev.lastKeystrokeAt !== null) {
          const delta = now - prev.lastKeystrokeAt;
          if (delta < CLAMP_MS) {
            // Autocomplete / stuck-key burst — ignore.
            return prev;
          }
          // If the tab was (or is) hidden, the gap is idle regardless of
          // length. Otherwise, split at IDLE_THRESHOLD_MS.
          const treatAsIdle =
            hiddenRef.current || delta > IDLE_THRESHOLD_MS;
          return {
            ...prev,
            lastKeystrokeAt: now,
            keystrokeCount: prev.keystrokeCount + 1,
            activeKeyingMs: prev.activeKeyingMs + (treatAsIdle ? 0 : delta),
            idleMs: prev.idleMs + (treatAsIdle ? delta : 0),
          };
        }
        // First keystroke of this document.
        return {
          ...prev,
          firstKeystrokeAt: now,
          lastKeystrokeAt: now,
          keystrokeCount: 1,
        };
      });
    },
    [args.active],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKeydown);
    return () => window.removeEventListener("keydown", onKeydown);
  }, [onKeydown]);

  // beforeunload flush — mirrors current state to the caller so a hard tab
  // close still emits a snapshot.
  const stateRef = useRef(state);
  stateRef.current = state;
  const onFlushRef = useRef(args.onFlush);
  onFlushRef.current = args.onFlush;
  useEffect(() => {
    const handler = () => {
      onFlushRef.current?.(stateRef.current);
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  return state;
}
